import { Grid, Modal, Paper } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import {
  Button,
  DatePicker,
  FilePresent,
  GeneralCardComponent,
  PageTitleAndSubTitle,
  Processing,
  TextField,
  TypableDropdown,
  Upload,
  UploadedDocument,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import SvgClose from '@HomaHealth/ui-component-library/lib/_components/atoms/Icons/Close';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import React, { useRef, useState } from 'react';
import ticketManagementConstants from '../../../core-utils/messages/TicketManagementConstants';
import { useStores } from '../../../stores';
import { observer } from 'mobx-react';
import { scrollBarStyles } from '../../../core-utils/messages/CommonConstants';
import ReportCreateNewLabelField from '../../atoms/ReportCreateNewLabelField';
import Dropdown from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Dropdown';
import { commonDropDownProps } from '../../atoms/ReportsCohortItem';
import { UserAccessConstants } from '../../../core-utils/helper-utils/userAccess';
import moment from 'moment';
import properties from '../../../core-utils/properties';
import axios from 'axios';
import { formatFileSize } from '../../../core-utils/helper-utils/TaskBoardHelper';

export interface NewTicketModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;

  handleSubmit: (data: any) => void;
  assigneeOptions: any[];
  assigneeValues: any[];
  handleOpenSnackBar: any;
}

export interface NewTicketData {
  issue: string;
  description: string;
  platform: string;
  type: string;
  priority: string;
  dueDate: string;
  assignee: string;
  status?: string;
  attachments: any[];
}

const NewTicketModal: React.FC<NewTicketModalProps> = observer(
  ({
    open,
    setOpen,
    handleSubmit,
    assigneeOptions,
    assigneeValues,
    handleOpenSnackBar,
  }) => {
    const useStyles = makeStyles()({
      modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      paper: {
        width: '676px',
        height: '650px',
        padding: '24px 28px 40px 32px',
        backgroundColor: patientAccessTheme.palette.status.white,
        border: 'none !important',
        position: 'relative',
      },
      button: {
        padding: '0px',
      },
      scroll: scrollBarStyles,
      avatar: { display: 'flex', gap: '5px', alignItems: 'center' },
    });
    const { classes } = useStyles();

    //Stores
    const loginPageStore = useStores().loginPageStore;

    //States
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [data, setData] = useState<NewTicketData>({
      issue: '',
      description: '',
      platform: '',
      type: '',
      priority: '',
      dueDate: '',
      assignee: '',
      attachments: [],
    });

    //Constants
    const messages = ticketManagementConstants.DETAILS_MODAL;
    const role = loginPageStore?.userResponse?.role?.name;
    const isAddTicketDisabled = !(
      data.issue &&
      data.platform &&
      data.type &&
      data.priority
    );
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    //functions
    const handleClose = () => {
      setOpen(false);
    };

    const handleDueDateChange = (

      date: Date | null,
      value?: any,
    ) => {
      setData((prevData) => ({
        ...prevData,
        dueDate: value || (date ? moment(date).format('MMMM D, YYYY') : ''),
      }));
    };

    const handleChange = (event: any, fieldName: string) => {
      const value = event.target.value as string;
      setData((prevData) => ({
        ...prevData,
        [fieldName.toLowerCase()]: value,
      }));
    };

    const handleFileChange = async (
      event: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const file = event.target.files?.[0];
      if (file) {
        setIsUploading(true);
        const formData = new FormData();
        formData.append(`file`, file);
        formData.append('userId', loginPageStore.userId);
        await axios
          .post(properties.aiqBaseUrl + properties.uploadAttachment, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          })
          .then((res: any) => {
            if (res.status === 200) {
              setData((prevData) => ({
                ...prevData,
                attachments: [...(prevData?.attachments || []), res.data],
              }));
            }
          })
          .catch((error: any) => {
            handleOpenSnackBar('Upload Failed', 'error', 6000);
            throw error;
          });
        setIsUploading(false);
      }
    };

    const handleButtonClick = () => {
      fileInputRef.current?.click();
    };

    return (
      <>
        <Modal open={open} onClose={handleClose} className={classes.modal}>
          <Paper className={classes.paper}>
            <Grid
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '30px',
              }}
            >
              <PageTitleAndSubTitle
                title={messages.title}
                subTitle={''}
                subTitleStyles={{ width: '0px' }}
              />
              {/* <SvgClose
                style={{ cursor: 'pointer' }}
                color={patientAccessTheme.palette.status.icon}
                onClick={handleClose}
              /> */}
            </Grid>
            <Grid
              className={classes.scroll}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '6px',
                overflowY: 'scroll',
                height: '476px',
              }}
            >
              <ReportCreateNewLabelField
                label={messages.issue}
                component={
                  <TextField
                    width="575px"
                    height="46px"
                    placeholder={messages.issue}
                    onChange={(event: any) =>
                      handleChange(event, messages.issue)
                    }
                    value={data.issue}
                    placeholderStyle={{
                      fontSize: '16px',
                    }}
                    error={!data.issue}
                  />
                }
                marginBottom="12px"
              />
              <ReportCreateNewLabelField
                label={messages.platform}
                component={
                  <Dropdown
                    label={messages.platform}
                    dropDownOptions={messages.platformOptions}
                    dropDownValues={messages.platformOptions}
                    value={data.platform}
                    setValue={(value: string) =>
                      setData((prevData) => ({ ...prevData, platform: value }))
                    }
                    selectStyles={{
                      ...commonDropDownProps.selectStyles,
                      width: '283px',
                    }}
                    labelTop="10px"
                    onChange={(event: any) =>
                      handleChange(event, messages.platform)
                    }
                    error={!data.platform}
                  />
                }
                marginBottom="12px"
              />
              <ReportCreateNewLabelField
                label={messages.type}
                component={
                  <TypableDropdown
                    dropDownOptions={messages.TypeOptions}
                    dropDownValues={messages.TypeOptions}
                    placeholder={messages.type}
                    value={data.type}
                    handleChange={(event) => {
                      handleChange(event, messages.type);
                    }}
                    handleInputChange={(value) =>
                      setData((prevData) => ({
                        ...prevData,
                        type: value,
                      }))
                    }
                    textFieldPlaceholder="Enter Type here (or) select from below"
                    height="36px"
                    width="283px"
                    error={!data.type}
                  />
                }
                marginBottom="12px"
              />
              <ReportCreateNewLabelField
                label={messages.description}
                component={
                  <TextField
                    width="575px"
                    placeholder={messages.descriptionPlaceholder}
                    onChange={(event: any) =>
                      handleChange(event, messages.description)
                    }
                    value={data.description}
                    multiline
                    placeholderStyle={{
                      fontSize: '16px',
                    }}
                  />
                }
                marginBottom="12px"
              />
              <ReportCreateNewLabelField
                label={messages.priority}
                component={
                  <Dropdown
                    label={messages.priority}
                    dropDownOptions={messages.priorityOptions}
                    dropDownValues={messages.priorityValues}
                    value={data.priority}
                    setValue={(value: string) =>
                      setData((prevData) => ({ ...prevData, priority: value }))
                    }
                    selectStyles={{
                      ...commonDropDownProps.selectStyles,
                      width: '283px',
                    }}
                    labelTop="10px"
                    onChange={(event: any) =>
                      handleChange(event, messages.priority)
                    }
                    error={!data.priority}
                  />
                }
              />
              {role === UserAccessConstants.SUPER_ADMIN && (
                <>
                  <ReportCreateNewLabelField
                    label={messages.dueDate}
                    component={
                      <DatePicker
                        width="283px"
                        handleChange={handleDueDateChange}
                        selectedDate={data.dueDate}
                        minDate={moment()}
                        maxDate={moment().add(1, 'years')}
                        format={'MMMM d, yyyy'}
                        placeholder={messages.dueDateFormat}
                      />
                    }
                  />
                  <ReportCreateNewLabelField
                    label={messages.assignee}
                    component={
                      <Dropdown
                        label={messages.assignee}
                        dropDownOptions={assigneeOptions}
                        dropDownValues={assigneeValues}
                        value={data.assignee}
                        setValue={(value: string) =>
                          setData((prevData) => ({
                            ...prevData,
                            assignee: value,
                          }))
                        }
                        selectStyles={{
                          ...commonDropDownProps.selectStyles,
                          width: '283px',
                        }}
                        labelTop="10px"
                        onChange={(event: any) =>
                          handleChange(event, messages.assignee)
                        }
                      />
                    }
                  />
                </>
              )}
              <GeneralCardComponent
                title={messages.attachments}
                titleFontSize="12px"
                titleFontType="medium"
                titleColor={patientAccessTheme.palette.text.mediumemphasis}
                titleRightMostItem={
                  <>
                    <Button
                      label={
                        <Typography
                          textColor={patientAccessTheme.palette.primary[500]}
                          fontSize="12px"
                          lineHeight="12px"
                          fontType={'medium'}
                        >
                          {isUploading ? messages.uploading : messages.upload}
                        </Typography>
                      }
                      startIcon={
                        isUploading ? (
                          <Processing
                            stroke={patientAccessTheme.palette.primary[500]}
                          />
                        ) : (
                          <Upload
                            stroke={patientAccessTheme.palette.primary[500]}
                          />
                        )
                      }
                      variant={'outlined'}
                      color="primary"
                      height="36px"
                      width={'fit-content'}
                      borderRadius="4px"
                      onClick={handleButtonClick}
                      disabled={isUploading}
                    />
                    <input
                      type="file"
                      ref={fileInputRef}
                      style={{ display: 'none' }}
                      onChange={handleFileChange}
                    />
                  </>
                }
                headerStyles={{
                  backgroundColor: patientAccessTheme.palette.status.white,
                  height: '76px',
                  justifyContent: 'center',
                }}
                bodyStyles={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '15px',
                  paddingTop: '0px',
                }}
                body={
                  <>
                    {data.attachments.map((item: any, index: number) => (
                      <UploadedDocument
                        name={item.fileName}
                        size={formatFileSize(item.fileSize)}
                        startIcon={
                          <FilePresent
                            stroke={patientAccessTheme.palette.primary[500]}
                            fill={patientAccessTheme.palette.primary[500]}
                          />
                        }
                        handleDelete={() => {
                          setData((prevData) => ({
                            ...prevData,
                            attachments: [
                              ...prevData.attachments.slice(0, index),
                              ...prevData.attachments.slice(index + 1),
                            ],
                          }));
                        }}
                      />
                    ))}
                  </>
                }
                mainGridStyles={{
                  border: '0px',
                  width: data.attachments.length > 0 ? '100%' : '60%',
                }}
              />
            </Grid>

            <Grid
              style={{
                display: 'flex',
                justifyContent: 'right',
                margin: '0px 40px 40px 0px',
                position: 'absolute',
                right: '0px',
                bottom: '0px',
              }}
            >
              <Button
                label={
                  <Typography
                    textColor={patientAccessTheme.palette.primary[500]}
                    fontSize="12px"
                    lineHeight="12px"
                    fontType={'medium'}
                  >
                    {messages.cancel}
                  </Typography>
                }
                variant="outlined"
                height="36px"
                width="180px"
                styleProps={{ marginRight: '25px' }}
                borderRadius="4px"
                className={classes.button}
                onClick={() => {
                  setOpen(false);
                }}
              />
              <Button
                label={
                  <Typography
                    textColor={patientAccessTheme.palette.status.white}
                    fontSize="12px"
                    lineHeight="12px"
                    fontType={'medium'}
                  >
                    {messages.addTicket}
                  </Typography>
                }
                variant="contained"
                color="primary"
                height="36px"
                width="180px"
                borderRadius="4px"
                className={classes.button}
                onClick={() => {
                  handleSubmit(data);
                  setOpen(false);
                }}
                disabled={isAddTicketDisabled || isUploading}
              />
            </Grid>
          </Paper>
        </Modal>
      </>
    );
  },
);

export default NewTicketModal;
